// @ts-nocheck
// src/components/gamification/Leaderboard.tsx
// Displays leaderboards for profiles and tribes
// Supports multiple timeframes and user rankings

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Trophy, Medal, Crown, TrendingUp, Users, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { GamificationBadge } from './GamificationBadge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useMutation } from 'convex/react';

interface LeaderboardProps {
	userId?: Id<'users'>;
	defaultType?: 'profile' | 'tribe';
	defaultTimeframe?: 'all_time' | 'weekly' | 'monthly';
	compact?: boolean;
	limit?: number;
}

interface LeaderboardEntry {
	_id: Id<'leaderboards'>;
	type: string;
	timeframe: string;
	entityId: string;
	entityType: string;
	rank: number;
	score: number;
	displayName: string;
	avatarUrl?: string;
	tier?: string;
	metrics: {
		followers: number;
		engagement: number;
		growthRate: number;
	};
	rewardEarned?: number;
	rewardClaimed?: boolean;
	weekStartDate?: string;
	weekEndDate?: string;
}

const rankIcons = {
	1: <Crown className="h-5 w-5 text-yellow-500" />,
	2: <Medal className="h-5 w-5 text-gray-400" />,
	3: <Trophy className="h-5 w-5 text-orange-600" />,
};

const timeframeLabels = {
	all_time: 'All Time',
	weekly: 'This Week',
	monthly: 'This Month',
};

export function Leaderboard({
	userId,
	defaultType = 'profile',
	defaultTimeframe = 'weekly',
	compact = false,
	limit = 10,
}: LeaderboardProps) {
	const { toast } = useToast();
	const [type, setType] = React.useState(defaultType);
	const [timeframe, setTimeframe] = React.useState(defaultTimeframe);
	const [claimingId, setClaimingId] = React.useState<string | null>(null);

	// Fetch leaderboard data
	const leaderboardData = useQuery(api.leaderboards.getLeaderboard, {
		type,
		timeframe,
		limit,
	});

	// Fetch user's rank
	const userRank = useQuery(
		api.leaderboards.getUserRank,
		userId ? { userId, type, timeframe } : 'skip'
	);

	// Mutation for claiming rewards
	const claimReward = useMutation(api.leaderboards.claimReward);

	const handleClaimReward = async (entry: LeaderboardEntry) => {
		if (!userId) return;
		
		try {
			setClaimingId(entry._id);
			await claimReward({
				leaderboardId: entry._id,
				userId,
			});
			
			toast({
				title: '🎉 Reward Claimed!',
				description: `You received ${entry.rewardEarned} KARMA tokens!`,
				duration: 5000,
			});
		} catch {
			toast({
				title: 'Error',
				description: 'Failed to claim reward',
				variant: 'destructive',
			});
		} finally {
			setClaimingId(null);
		}
	};

	const renderEntry = (entry: LeaderboardEntry, index: number) => {
		const isUserEntry = userRank && userRank._id === entry._id;
		const canClaim = isUserEntry && entry.rewardEarned && !entry.rewardClaimed && timeframe === 'weekly';

		return (
			<motion.div
				key={entry._id}
				initial={{ opacity: 0, x: -20 }}
				animate={{ opacity: 1, x: 0 }}
				transition={{ duration: 0.3, delay: index * 0.05 }}
				className={cn(
					"flex items-center gap-4 p-4 rounded-lg transition-all",
					"hover:bg-gray-800/50",
					isUserEntry && "bg-purple-900/20 border border-purple-500/30"
				)}
			>
				{/* Rank */}
				<div className="flex items-center justify-center w-12">
					{rankIcons[entry.rank as keyof typeof rankIcons] || (
						<span className="text-xl font-bold text-gray-400">
							#{entry.rank}
						</span>
					)}
				</div>

				{/* Avatar/Image */}
				<Avatar className="h-12 w-12">
					<AvatarImage src={entry.avatarUrl} alt={entry.displayName} />
					<AvatarFallback className="bg-gradient-to-br from-purple-600 to-pink-600">
						{entry.displayName.slice(0, 2).toUpperCase()}
					</AvatarFallback>
				</Avatar>

				{/* Details */}
				<div className="flex-1">
					<div className="flex items-center gap-2">
						<h4 className="font-semibold text-foreground">
							{entry.displayName}
						</h4>
						{isUserEntry && (
							<Badge variant="outline" className="text-xs">
								You
							</Badge>
						)}
					</div>
					{!compact && (
						<div className="flex items-center gap-4 mt-1 text-sm text-gray-400">
							<span className="flex items-center gap-1">
								<Users className="h-3 w-3" />
								{entry.metrics.followers.toLocaleString()}
							</span>
							{entry.metrics.growthRate > 0 && (
								<span className="flex items-center gap-1 text-green-400">
									<TrendingUp className="h-3 w-3" />
									+{entry.metrics.growthRate.toFixed(1)}%
								</span>
							)}
						</div>
					)}
				</div>

				{/* Score & Tier */}
				<div className="flex items-center gap-3">
					<GamificationBadge
						score={entry.score}
						tier={entry.tier || (type === 'profile' ? 'bronze' : 'common')}
						size="sm"
						showScore={!compact}
					/>
					
					{/* Reward claim button */}
					{canClaim && (
						<Button
							onClick={() => handleClaimReward(entry)}
							disabled={claimingId === entry._id}
							size="sm"
							variant="outline"
							className="ml-2"
						>
							{claimingId === entry._id ? (
								<>
									<div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2" />
									Claiming...
								</>
							) : (
								<>
									🎁 Claim {entry.rewardEarned} KARMA
								</>
							)}
						</Button>
					)}
				</div>
			</motion.div>
		);
	};

	if (compact) {
		return (
			<Card className="bg-gray-900/50 border-gray-800">
				<CardHeader>
					<CardTitle className="text-lg flex items-center justify-between">
						<span className="flex items-center gap-2">
							<Trophy className="h-5 w-5 text-yellow-500" />
							Top {type === 'profile' ? 'Profiles' : 'Tribes'}
						</span>
						<Select value={timeframe} onValueChange={setTimeframe}>
							<SelectTrigger className="w-32 h-8 text-sm">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all_time">All Time</SelectItem>
								<SelectItem value="weekly">Weekly</SelectItem>
								<SelectItem value="monthly">Monthly</SelectItem>
							</SelectContent>
						</Select>
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-2">
					{leaderboardData?.entries.map((entry, index) =>
						renderEntry(entry, index)
					)}
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Trophy className="h-6 w-6 text-yellow-500" />
					Leaderboards
				</CardTitle>
			</CardHeader>
			<CardContent>
				<Tabs value={type} onValueChange={(v) => setType(v as typeof type)}>
					<div className="flex items-center justify-between mb-6">
						<TabsList>
							<TabsTrigger value="profile">Profiles</TabsTrigger>
							<TabsTrigger value="tribe">Tribes</TabsTrigger>
						</TabsList>
						
						<Select value={timeframe} onValueChange={setTimeframe}>
							<SelectTrigger className="w-40">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all_time">
									<span className="flex items-center gap-2">
										<Clock className="h-4 w-4" />
										All Time
									</span>
								</SelectItem>
								<SelectItem value="weekly">
									<span className="flex items-center gap-2">
										<Clock className="h-4 w-4" />
										This Week
									</span>
								</SelectItem>
								<SelectItem value="monthly">
									<span className="flex items-center gap-2">
										<Clock className="h-4 w-4" />
										This Month
									</span>
								</SelectItem>
							</SelectContent>
						</Select>
					</div>

					<AnimatePresence mode="wait">
						<motion.div
							key={`${type}-${timeframe}`}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							exit={{ opacity: 0, y: -20 }}
							transition={{ duration: 0.3 }}
						>
							<TabsContent value={type} className="space-y-2 mt-0">
								{leaderboardData?.entries.length === 0 ? (
									<div className="text-center py-12 text-gray-500">
										No entries yet for {timeframeLabels[timeframe].toLowerCase()}
									</div>
								) : (
									leaderboardData?.entries.map((entry, index) =>
										renderEntry(entry, index)
									)
								)}
								
								{/* User's rank if not in top entries */}
								{userRank && userRank.rank > limit && (
									<>
										<div className="text-center py-2 text-gray-500">
											• • •
										</div>
										{renderEntry(userRank as LeaderboardEntry, limit)}
									</>
								)}
							</TabsContent>
						</motion.div>
					</AnimatePresence>
				</Tabs>

				{/* Week dates for weekly leaderboard */}
				{timeframe === 'weekly' && leaderboardData?.entries[0]?.weekStartDate && (
					<div className="mt-4 text-center text-sm text-gray-500">
						Week of {new Date(leaderboardData.entries[0].weekStartDate).toLocaleDateString()} - 
						{' '}{new Date(leaderboardData.entries[0].weekEndDate!).toLocaleDateString()}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
